import * as React from "react";
import { observer, Observer } from "mobx-react-lite";
import { Button, Dropdown, MenuProps, message, Tabs } from "antd";
import WrappedResetPasswordForm from "../../user_profile/components/ResetPasswordDialog";
import Form, { useForm } from "antd/lib/form/Form";
import { reaction } from "mobx";
import { UserModel } from "../types/UserModel";
import UsersManagerStore, { LoginTypes } from "../stores/UsersManagerStore";
import { UserForm } from "./UserForm";
import { ProjectsAssignmentTable } from "./ProjectsAssignmentTable";
import { AppRoles } from "src/modules/authorization/Permissions";
import AppModal from "../../common/components/AppModal";
import DeleteUserDialog from "./DeleteUserDialog";

type Props = {
    store: UsersManagerStore;
};

export const EditUserDialog: React.FC<Props> = observer(({ store }) => {
    const [form] = useForm();
    const [deleteConfirmVisible, setDeleteConfirmVisible] =
        React.useState(false);
    const [activeTab, setActiveTab] = React.useState("user-form");

    const mapUserRoles = (user: UserModel) => {
        const defaultRoles = store!.sortedRoles.map((r) => r.name);
        const defaultUserRoles = user.roles
            .filter((r) => defaultRoles.includes(r.name))
            .map((r) => r.name);
        const customRoles = user.roles
            .filter((r) => !defaultRoles.includes(r.name))
            .map((r) => r.id);

        return [...defaultUserRoles, ...customRoles];
    };

    React.useEffect(() => {
        const disposer = reaction(
            () => store!.selectedUser,
            (user: UserModel) => {
                form.setFieldsValue({
                    userName: user!.userName,
                    email: user!.email,
                    firstName: user!.firstName,
                    lastName: user!.lastName,
                    role: mapUserRoles(user!)[0],
                    loginType: user!.isDomainUser
                        ? LoginTypes.domainUser
                        : LoginTypes.internalUser,
                    projects: user!.projects,
                });
            },
        );
        return () => {
            disposer();
        };
    }, [form, store]);

    const validateFields = () => {
        return form.validateFields().then(async (data) => {
            if (data.loginType === LoginTypes.domainUser) {
                data.userName = store!.selectedUser!.id;
                data.isDomainUser = true;
            } else {
                data.email = data.email.toLowerCase();
            }
            return data;
        });
    };

    const handleSaveClick = async () => {
        try {
            // Validate form data
            const formData = await validateFields();

            const isAdminBeingDemoted =
                store
                    .selectedUser!.roles.map((r) => r.name)
                    .includes("admin") && store.currentEditableRole === "user";

            // Special case: if admin is being demoted and we're on Details tab,
            // navigate to Project Access tab first
            if (activeTab === "user-form" && isAdminBeingDemoted) {
                store.setNewUserFormValues(formData);
                store.setEditableProjectsForEditableUser();
                setActiveTab("projects");
                return;
            }

            // Validate project selection for non-admin users
            if (
                store.editableProjectsForUser.length === 0 &&
                store.currentEditableRole !== AppRoles.admin
            ) {
                message.error("Please select at least one project");
                return;
            }

            await store.createUpdateUser(formData as UserModel, true);
            store.setIsEditUserDialogVisible(false);
            store.setCurrentEditableRole(null);
            store.clearEditableProjects();
            store.loadProjects();
        } catch (error) {
            if (activeTab === "projects") {
                setActiveTab("user-form");
            }
            console.error("Error saving user:", error);
        }
    };

    const handleCancel = () => {
        store!.setIsEditUserDialogVisible(false);
        store.setCurrentEditableRole(null);
        store.clearEditableProjects();
        form.resetFields();
        setActiveTab("user-form");
    };

    const deleteConfirm = () => {
        setDeleteConfirmVisible(true);
    };
    const isDomainUser = store!.selectedUser?.isDomainUser;

    const menu: MenuProps = {
        items: [
            {
                key: 1,
                label: (
                    <div
                        data-id="edit-user-dialog-reset-pwd"
                        data-testid="edit-user-dialog-reset-pwd"
                        onClick={() => {
                            if (!isDomainUser) {
                                store!.setResetPasswordDialogVisible(true);
                            }
                        }}
                    >
                        Reset password
                    </div>
                ),
                disabled: isDomainUser,
            },
            {
                key: 2,
                label: (
                    <div
                        data-id="edit-user-dialog-remove"
                        data-testid="edit-user-dialog-remove"
                        style={{ color: "red" }}
                        onClick={deleteConfirm}
                    >
                        Remove user
                    </div>
                ),
                disabled: store.currentUserId === store.selectedUser!.id,
            },
        ],
    };

    const deleteUser = () => {
        store!.deleteUser();
        setDeleteConfirmVisible(false);
        store.setCurrentEditableRole(null);
        store.clearEditableProjects();
    };

    const getFooter = () => {
        return activeTab === "user-form"
            ? [
                  <div
                      key="edit-user-footer-actions"
                      style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          width: "100%",
                      }}
                  >
                      <div>
                          <Dropdown
                              menu={menu}
                              trigger={["click"]}
                              key="more"
                              overlayClassName="alpha-dropdown-menu"
                          >
                              <Button
                                  className="light"
                                  data-id="edit-user-dialog-more"
                                  data-testid="edit-user-dialog-more"
                                  size="large"
                                  disabled={store.tableIsLoading}
                              >
                                  More...
                              </Button>
                          </Dropdown>
                      </div>
                      <div
                          style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              flex: 1,
                              alignItems: "center",
                              gap: 8,
                          }}
                      >
                          <Button
                              data-id="edit-user-dialog-cancel"
                              size="large"
                              key="cancel"
                              onClick={handleCancel}
                              disabled={store.tableIsLoading}
                          >
                              Cancel
                          </Button>
                          <Button
                              data-id="edit-user-dialog-submit"
                              data-testid="edit-user-dialog-submit"
                              size="large"
                              key="submit"
                              type="primary"
                              onClick={handleSaveClick}
                              loading={store.tableIsLoading}
                              className="no-margin"
                          >
                              Save changes
                          </Button>
                      </div>
                  </div>,
              ]
            : [
                  <div
                      key="edit-user-footer-actions"
                      style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          gap: 8,
                      }}
                  >
                      <Button
                          className="light"
                          data-id="add-user-dialog-cancel"
                          size="large"
                          key="cancel"
                          onClick={handleCancel}
                          disabled={store.tableIsLoading}
                      >
                          Cancel
                      </Button>
                      <Button
                          data-id="add-user-dialog-submit"
                          size="large"
                          key="submit"
                          type="primary"
                          onClick={handleSaveClick}
                          loading={store.tableIsLoading}
                      >
                          Save changes
                      </Button>
                  </div>,
              ];
    };

    const { selectedUser } = store!;

    const tabItems = [
        {
            key: "user-form",
            label: "Details",
            children: (
                <Form
                    data-id="edit-user-content-form"
                    form={form}
                    className="alpha-form"
                    layout="vertical"
                    initialValues={
                        (selectedUser && {
                            userName: selectedUser!.userName,
                            email: selectedUser!.email!,
                            firstName: selectedUser!.firstName,
                            lastName: selectedUser!.lastName,
                            role:
                                selectedUser!.roles &&
                                selectedUser!.roles.length
                                    ? selectedUser!.roles.map((x) => x.name)[0]
                                    : "",
                            loginType: selectedUser!.isDomainUser
                                ? LoginTypes.domainUser
                                : LoginTypes.internalUser,
                            projects: selectedUser.projects ?? [],
                        }) ||
                        {}
                    }
                >
                    <UserForm store={store} form={form} />
                </Form>
            ),
        },
        {
            key: "projects",
            label: "Project access",
            children: <ProjectsAssignmentTable store={store} />,
        },
    ];

    return (
        <AppModal
            open={store!.isEditUserDialogVisible}
            onCancel={handleCancel}
            maskClosable={false}
            closable={false}
            title="Edit user"
            destroyOnClose
            width={660}
            centered
            className="alpha-modal"
            footer={getFooter()}
        >
            <Observer>
                {() => (
                    <>
                        <DeleteUserDialog
                            open={deleteConfirmVisible}
                            user={store.selectedUser}
                            onCancel={() => setDeleteConfirmVisible(false)}
                            onConfirm={deleteUser}
                        />
                        <WrappedResetPasswordForm store={store} />
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            items={tabItems}
                        />
                    </>
                )}
            </Observer>
        </AppModal>
    );
});

export default EditUserDialog;
